import React, { useEffect } from "react";
import { FaCartPlus, FaStore, FaTimes } from "react-icons/fa";

import {
  FALLBACK_ARTICLE_IMAGE,
  formatEUR,
  getArticleImage,
  getStockBadgeClass,
  getStockLabel,
  isOutOfStock,
} from "../utils/boutiqueUtils";

export default function BoutiqueArticleDrawer({
  isOpen,
  article,
  onClose,
  onAdd,
}) {
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !article) return null;

  const image = getArticleImage(article);
  const out = isOutOfStock(article);

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="relative z-[121] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl dark:bg-slate-950 sm:max-w-4xl sm:rounded-[2rem]">
        <header className="relative overflow-hidden bg-slate-950 px-5 py-5 text-white sm:px-7">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-sky-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-44 w-44 rounded-full bg-lime-500/30 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-sky-200">
                <FaStore />
                Détail article
              </p>

              <h2 className="mt-2 line-clamp-2 text-xl font-bold sm:text-2xl">
                {article.nom || "Article"}
              </h2>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_330px]">
            <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
              <img
                src={image}
                alt={article.nom || "Article"}
                className="max-h-[560px] w-full object-contain"
                onError={(event) => {
                  event.currentTarget.src = FALLBACK_ARTICLE_IMAGE;
                }}
              />
            </div>

            <div className="space-y-4">
              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Prix
                </p>

                <p className="mt-2 text-3xl font-bold text-slate-950 dark:text-white">
                  {formatEUR(article.prix)}
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {article.categorie && (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                      {article.categorie}
                    </span>
                  )}

                  {article.taille && (
                    <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                      Taille : {article.taille}
                    </span>
                  )}

                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ring-1 ${getStockBadgeClass(
                      article
                    )}`}
                  >
                    {getStockLabel(article)}
                  </span>
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Description
                </p>

                <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                  {article.description || "Aucune description disponible."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <footer className="sticky bottom-0 border-t border-slate-200 bg-white/95 p-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:p-4">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={() => onAdd?.(article)}
              disabled={out}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FaCartPlus />
              {out ? "Stock épuisé" : "Ajouter"}
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}